<script setup>
/**
 * Turn on, in place (owner, 3 Nov 2026, SQUASH_LEVELS_CARDS_PLAN.md): the
 * Squash Levels question over the page, so a member sees what is sent before
 * they agree. Never flips silently.
 *
 * Opened from your own player sheet, your own profile, and the match and
 * challenge pages' "you" row. Fetches GET competitionQuestions/account/squashLevels
 * (the question as it stands, whatever they answered), starts the switch ON,
 * and saves through PUT auth/me/externalServices. The X changes nothing.
 *
 * Emits `saved(on)` once the API has taken the answer.
 */
import {ref, watch} from 'vue';
import {endpoints} from '~/network/endpoints.js';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {useAuthService} from '~/composables/useAuthService';
import {SQUASH_LEVELS_QUESTION_TITLE, accountQuestionFrom, squashLevelsServicesBody, squashLevelsTurnOnTicks} from '~/composables/useSquashLevelsCards';
import CompetitionQuestionsDialog from '~/components/competitions/CompetitionQuestionsDialog.vue';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  questionKey: {type: String, default: 'squashLevels'},
});
const emit = defineEmits(['update:modelValue', 'saved']);

const {httpRequest} = useHttpRequest();
const {updateLocalUser} = useAuthService();

const questions = ref([]);
const ticked = ref({});
const loading = ref(false);
const saving = ref(false);
const error = ref(null);

const load = async () => {
  loading.value = true;
  error.value = null;
  questions.value = [];
  try {
    const q = accountQuestionFrom(await httpRequest(endpoints.competitionQuestions.account(props.questionKey)));
    if (!q) throw new Error('no question');
    questions.value = [q];
    ticked.value = squashLevelsTurnOnTicks(q);
  } catch (e) {
    error.value = e?.global?.[0] || "Couldn't load that. Please try again.";
  } finally {
    loading.value = false;
  }
};

watch(() => props.modelValue, (open) => {
  if (open && !import.meta.server) load();
}, {immediate: true});

const save = async () => {
  const q = questions.value[0];
  if (!q || saving.value) return;
  const on = ticked.value[q.key] === true;
  saving.value = true;
  error.value = null;
  try {
    await httpRequest(endpoints.auth.meServices, squashLevelsServicesBody(on));
    await updateLocalUser();
    emit('saved', on);
    emit('update:modelValue', false);
  } catch (e) {
    error.value = e?.global?.[0] || "Couldn't save that. Please try again.";
  } finally {
    saving.value = false;
  }
};

const onModel = (value) => {
  if (!value && !saving.value) emit('update:modelValue', false);
};
</script>

<template>
  <competition-questions-dialog :model-value="modelValue" @update:model-value="onModel" v-model:ticked="ticked"
                                :questions="questions" :title="SQUASH_LEVELS_QUESTION_TITLE" :loading="loading" :saving="saving" :error="error"
                                @save="save"/>
</template>
